interface ProxyOptions {
  placeholder?: string;
  debug?: boolean;
  noCache?: boolean;
  attempt?: number;
}

/**
 * Converts an external image URL to a proxied URL that avoids CORS issues
 * and provides fallback for failed images
 */
export function getProxiedImageUrl(
  url: string | undefined | null,
  options: ProxyOptions = {}
): string {
  const {
    placeholder = '/images/news-placeholder.jpg',
    debug = false,
    noCache = true, // Default to no cache to avoid stale images
    attempt = 0
  } = options;

  // If no URL is provided, return placeholder
  if (!url) {
    console.log('getProxiedImageUrl: No URL provided, returning placeholder');
    return placeholder;
  }

  // If the URL is already a relative path, data URL, or blob URL, return it as is
  if (url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('chrome-extension:')) {
    console.log('getProxiedImageUrl: URL is already local or data URL, using as-is:', url);
    return url;
  }

  // Handle relative URLs without proper protocol (like "layout/images/wiz_logo.png")
  if (url.startsWith('layout/') || 
      (!url.startsWith('http') && !url.startsWith('/') && 
       !url.startsWith('data:') && !url.startsWith('blob:'))) {
    console.log('getProxiedImageUrl: Detected invalid relative URL:', url);
    return placeholder;
  }

  // Clean the URL by removing spaces and special characters
  const cleanedUrl = url.trim().replace(/\s+/g, '');

  try {
    // Validate the URL
    const parsedUrl = new URL(cleanedUrl);
    
    // List of all domains that should use the proxy
    const proxyDomains = [
      'alphanews.live',
      'politis.com.cy',
      'philenews.com',
      'cyprustimes.com',
      'protathlima.cyprustimes.com',
      'shootandgoal.cyprustimes.com',
      'eurokerdos.cyprustimes.com',
      'knews.kathimerini.com.cy'
    ];
    
    // Check if the URL is from a domain that needs proxy
    const needsProxy = proxyDomains.some(domain => cleanedUrl.includes(domain));
    
    if (needsProxy) {
      // Identify specific problematic domains
      const isCyprusTimes = cleanedUrl.includes('cyprustimes.com');
      const isProtathlima = cleanedUrl.includes('protathlima.cyprustimes.com');
      const isShootandgoal = cleanedUrl.includes('shootandgoal.cyprustimes.com');
      const isEurokerdos = cleanedUrl.includes('eurokerdos.cyprustimes.com');
      const isKathimerini = cleanedUrl.includes('knews.kathimerini.com.cy');
      
      // Identify the most problematic domains that need special handling
      const isProblemDomain = isProtathlima || isShootandgoal || isEurokerdos || isKathimerini;
      
      // Create a cache-busting timestamp
      const timestamp = Date.now().toString();
      
      // Always use debug mode for known problematic domains
      const debugFlag = debug || isProblemDomain ? '&debug=true' : '';
      
      // Enable no-cache for problematic domains to avoid caching issues
      const noCacheFlag = noCache || isProblemDomain ? '&noCache=true' : '';
      
      // Include the attempt parameter for tracking fallback attempts
      const attemptParam = isProblemDomain ? `&attempt=${attempt}` : '';
      
      // Build the final proxy URL
      console.log(`getProxiedImageUrl: Using proxy for domain: ${parsedUrl.hostname}${isProblemDomain ? ` (attempt: ${attempt})` : ''}`);
      return `/api/image-proxy?url=${encodeURIComponent(cleanedUrl)}&t=${timestamp}${debugFlag}${noCacheFlag}${attemptParam}`;
    }
    
    // For all other domains, use the URL directly
    console.log('getProxiedImageUrl: Using direct URL for:', cleanedUrl);
    return cleanedUrl;
    
  } catch (error) {
    console.error('getProxiedImageUrl: Invalid URL format:', url, error instanceof Error ? error.message : error);
    return placeholder;
  }
} 